// AUDITUI.A2 (#2587) — one audit event as a row. The FALLBACK IS THE PRIMARY
// PATH (D1): the sentence comes from buildAuditSentence(), which uses a
// registered builder only if the injected registry has one, and otherwise the
// generic fallback. The registry ships empty (D2), so today every row here is
// a fallback row, and that is the designed behaviour, not a degradation.
//
// Severity comes from classifyAuditSeverity in packages/shared (D5/S2). There
// is no second classifier in this file and must never be one: the drawer (A3)
// and the Activity page (A5) would then disagree about what counts as
// governance.
//
// Badge styling follows DeliveryModeBadge's token-plus-rgba idiom (D4):
// foreground is a var() token, background/border are that token's hex at
// reduced alpha. The alpha literals restate the light-theme token, exactly as
// DeliveryModeBadge does — see that file before "fixing" them.
//
// Colour tokens: --text-secondary, never --text-muted, which is undefined in
// index.css despite 18 files using it.

import { buildAuditSentence, classifyAuditSeverity } from './auditShared';
import type { AuditEvent, AuditRendererRegistry } from './auditShared';
import RedactionMarker from './RedactionMarker';
import { formatTimeAgo } from '../../lib/dashboardFormat';

interface Props {
  event: AuditEvent;
  /** Injected, never module state — see auditShared.ts and AC6. */
  registry?: AuditRendererRegistry;
  /** Injectable clock, matching formatTimeAgo's own test seam. */
  now?: number;
}

// ── Focus ring (AC8) — one-shot module-level stylesheet injection ──────────
// :focus-visible cannot be expressed as an inline style, and every other style
// in this component is inline. Rather than invent a second idiom, this follows
// HelpPopover.tsx:33-55, which solved the same problem the same way.
//
// :focus-visible, not :focus — a mouse click on a row should not leave a ring
// behind; only keyboard traversal should.
//
// outline-offset is NEGATIVE here, where HelpPopover uses +2px: these rows are
// full-bleed and stacked, so an outward ring would overlap the neighbouring
// row's border. Drawing it inside keeps it legible without moving anything.
//
// --primary is an existing token (index.css:8), and it is NOT overridden in the
// dark block (index.css:68-81 overrides --bg/--surface/--border/--text/… but
// never --primary), so it is theme-invariant. The hex fallback restates that one
// value exactly as HelpPopover's does; it is not a new colour.
//
// The selector keys on data-audit-row, a DEDICATED styling hook, not on
// data-testid. R2 caught the original here keying on data-testid and pointed out
// that HelpPopover — the precedent this cites — does the opposite: its elements
// carry both data-testid="help-popover-trigger" (:503) AND
// data-help-popover-trigger="" (:504), and the stylesheet selects on the latter
// (:48-49). Production styling should not depend on a test attribute surviving.
let auditTrailStylesInjected = false;
function injectAuditTrailStylesOnce(): void {
  if (auditTrailStylesInjected) return;
  if (typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-audit-trail-styles', '');
  style.textContent = `
[data-audit-row]:focus-visible {
  outline: 2px solid var(--primary, #7C3AED);
  outline-offset: -2px;
  border-radius: 2px;
}
`;
  document.head.appendChild(style);
  auditTrailStylesInjected = true;
}

const GOVERNANCE_STYLE = {
  color:      'var(--warning)',              // #F59E0B
  background: 'rgba(245, 158, 11, 0.12)',    // --warning at 12% alpha
  border:     '1px solid rgba(245, 158, 11, 0.35)',
} as const;

const ROUTINE_STYLE = {
  color:      'var(--text-secondary)',       // #78716C light / #A8A29E dark
  background: 'rgba(120, 113, 108, 0.10)',   // --text-secondary at 10% alpha
  border:     '1px solid rgba(120, 113, 108, 0.30)',
} as const;

export default function AuditEventRow({ event, registry, now }: Props) {
  injectAuditTrailStylesOnce();

  const sentence = buildAuditSentence(event, registry);
  const severity = classifyAuditSeverity(event.action);
  const badge    = severity === 'governance' ? GOVERNANCE_STYLE : ROUTINE_STYLE;

  return (
    <li
      data-testid="audit-event-row"
      data-severity={severity}
      data-audit-row=""
      // AC8: each row is its own tab stop, in DOM order. tabIndex=0 rather
      // than a roving tabindex because there is nothing to expand yet — a
      // roving pattern would be machinery for a list that has no second
      // interaction. Revisit when AC5's diff expanders land (#2810).
      //
      // No onKeyDown here, deliberately: intercepting Tab is how a list
      // becomes a focus trap. The browser's own traversal carries focus in
      // and out of the list.
      tabIndex={0}
      style={{
        display:       'flex',
        flexDirection: 'column',
        gap:           4,
        padding:       '10px 0',
        borderBottom:  '1px solid var(--border)',
        listStyle:     'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--text)', fontFamily: 'var(--font-body)' }}>
          <strong data-testid="audit-row-actor" style={{ fontWeight: 600 }}>{sentence.actor}</strong>
          {' '}
          <span data-testid="audit-row-verb">{sentence.verb}</span>
          {sentence.object && (
            <>
              {' '}
              <span data-testid="audit-row-object" style={{ color: 'var(--text-secondary)' }}>
                {sentence.object}
              </span>
            </>
          )}
        </span>

        <span
          data-testid="audit-row-severity"
          style={{
            ...badge,
            fontSize:      10,
            fontWeight:    600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            borderRadius:  999,
            padding:       '1px 7px',
            fontFamily:    'var(--font-body)',
          }}
        >
          {severity}
        </span>

        <time
          data-testid="audit-row-time"
          dateTime={event.timestamp}
          style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 'auto' }}
        >
          {formatTimeAgo(event.timestamp, now)}
        </time>
      </div>

      {(event.reason || event.redactedFields.length > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {event.reason && (
            <span
              data-testid="audit-row-reason"
              style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}
            >
              {event.reason}
            </span>
          )}
          <RedactionMarker fields={event.redactedFields} />
        </div>
      )}
    </li>
  );
}
